import { useState, useMemo, useRef, useEffect } from 'react'
import styled, { useTheme } from 'styled-components'
import { Modal, Form, Select, DatePicker, Tooltip } from 'antd'
import dayjs from 'dayjs'
import {
  Arrow,
  FormCard,
  FormItem,
  Heading,
  Input,
  TextArea
} from '@lmc/ui-kit'
import { useTranslation } from 'react-i18next'
import { NAMESPACE } from 'locale'
import { showToast } from 'common/utils'
import Delivery from '../../iconsV2/AddStock'
import { Button, Text } from '@lmc/ui-kit/src/general'
import QuestionMarkIcon from '@lmc/ui-kit/src/general/MoreInfoLink/QuestionMarkIcon'
import Divider from '@lmc/ui-kit/src/layout/Divider/Divider'
import Asterix from 'screens/Rostering/RotaPattern/components/Asterix'
import StockBanner from './StockBanner'
import AdditionalDetailsSection from './AdditionalDetailsSection'
import { useGroupedCarers } from 'hooks/useGroupedOptions'
import { StockRecord, AddStockData } from '../../types'
import { convertMediaAttachmentsToFileList } from '../../utils/stockRecordUtils'
import useAddStock from '../../hooks/useAddStock'

type AddStockModalProps = {
  open: boolean
  medicationName: string
  expectedStock: number
  residentId: string
  residentMedicationId: string
  unit?: string
  onCancel: () => void
  onSuccess?: () => void
  editMode?: boolean
  record?: StockRecord
}

const AddStockModal = ({
  open,
  medicationName,
  expectedStock,
  residentId,
  residentMedicationId,
  unit,
  onCancel,
  onSuccess,
  editMode = false,
  record
}: AddStockModalProps) => {
  const { t } = useTranslation(NAMESPACE.EMAR_V2)
  const theme = useTheme()
  const [form] = Form.useForm()
  const displayUnit = record?.unit || unit || ''

  const actualExpectedStock =
    editMode && record
      ? (record.expectedStock ?? 0) - (record.quantityChange ?? 0)
      : expectedStock

  const getInitialData = (record: StockRecord | undefined) => {
    if (!record) return undefined

    const description = Array.isArray(record.notes)
      ? record.notes.join(', ')
      : record.notes || ''
    return {
      recordId: record.id,
      description,
      receivedStock: record.quantityChange,
      newStock: record.stockCount,
      isStockAccurate: false,
      receivedBy: record.receivedBy?.id || '',
      actionTime: record.actionTime ? dayjs(record.actionTime) : undefined,
      resolveDiscrepancy: null,
      discrepancyReason: '',
      receivedFrom: record.receivedFrom || '',
      witnessedBy: '',
      witnessPin: '',
      additionalDetails: false
    }
  }

  const initialData = getInitialData(record)

  const initialValues = {
    description: initialData?.description || '',
    receivedBy: initialData?.receivedBy || undefined,
    actionTime: initialData?.actionTime,
    receivedFrom: initialData?.receivedFrom || ''
  }

  const [receivedStock, setReceivedStock] = useState<number | null>(
    editMode && initialData?.receivedStock !== undefined
      ? initialData.receivedStock
      : null
  )

  const hasMedia = editMode && record?.mediaAttachments?.length

  const [fileList, setFileList] = useState<any[]>(() => {
    if (!hasMedia) return []
    return convertMediaAttachmentsToFileList(
      record!.mediaAttachments!,
      record!.id
    )
  })

  const [showAdditionalDetails, setShowAdditionalDetails] = useState(!!hasMedia)
  const [adjustmentError, setAdjustmentError] = useState<string | null>(null)
  const [resolveDiscrepancy, setResolveDiscrepancy] = useState<string | null>(
    null
  )
  const [discrepancyReason, setDiscrepancyReason] = useState<string>('')

  // Track initial values for dirty detection
  const initialFileListRef = useRef<any[]>([])
  const initialShowAdditionalDetailsRef = useRef<boolean>(false)
  const initialReceivedStockRef = useRef<number | null>(null)
  const prevOpenRef = useRef<boolean>(false)

  useEffect(() => {
    // Only capture initial values when modal transitions from closed to open
    if (open && !prevOpenRef.current) {
      // Deep copy fileList to avoid reference issues
      initialFileListRef.current = fileList.map((file) => ({ ...file }))
      initialShowAdditionalDetailsRef.current = showAdditionalDetails
      initialReceivedStockRef.current = receivedStock
    }
    prevOpenRef.current = open
  }, [open])

  const { groupedCarerOptions } = useGroupedCarers()
  const carerOptions = useMemo(() => {
    return groupedCarerOptions.flatMap((group) => group.options)
  }, [groupedCarerOptions])

  const { createAddStock, updateAddStock, isLoading } = useAddStock({
    residentId,
    residentMedicationId,
    medicationName,
    onSuccess: () => {
      showToast({
        type: 'success',
        title: editMode
          ? t('ADD_STOCK_UPDATED_SUCCESS')
          : t('ADD_STOCK_CREATED_SUCCESS')
      })
      onSuccess?.()
    },
    onError: () => {
      showToast({
        type: 'error',
        title: t('ADD_STOCK_ERROR')
      })
    }
  })

  const handleFileChange = (info: any) => {
    setFileList(info.fileList)
  }

  const handleReceivedStockChange = (value: string) => {
    if (!value || value === '') {
      setReceivedStock(null)
      setAdjustmentError(null)
      return
    }

    const numVal = Number(value)

    if (isNaN(numVal)) {
      setReceivedStock(null)
      setAdjustmentError(null)
      return
    }

    if (numVal <= 0) {
      setAdjustmentError(t('INVALID_INPUT'))
      setReceivedStock(null)
    } else {
      setAdjustmentError(null)
      setReceivedStock(numVal)
    }
  }

  const handleSave = () => {
    form.validateFields().then((values) => {
      const newStock = actualExpectedStock + (receivedStock ?? 0)
      const mediaFiles = fileList
        .map((file: any) => file.originFileObj)
        .filter(Boolean) as File[]

      const currentFileKeys = new Set(
        fileList
          .filter((file: any) => !file.originFileObj && file.key)
          .map((file: any) => file.key)
      )
      const removedKeys =
        editMode && record?.mediaAttachments
          ? record.mediaAttachments
              .filter((attachment) => !currentFileKeys.has(attachment.key))
              .map((attachment) => attachment.key)
          : []

      const data: AddStockData = {
        description: values.description || '',
        receivedStock: receivedStock ?? 0,
        newStock: newStock,
        isStockAccurate: false,
        receivedBy: values.receivedBy || '',
        actionTime: values.actionTime || undefined,
        resolveDiscrepancy,
        discrepancyReason,
        receivedFrom: values.receivedFrom || '',
        witnessedBy: values.witnessedBy || '',
        witnessPin: values.witnessPin || '',
        additionalDetails: showAdditionalDetails,
        media: mediaFiles,
        mediaAttachmentKeysToRemove:
          removedKeys.length > 0 ? removedKeys : undefined
      }

      if (editMode && record?.id) {
        updateAddStock({ recordId: record.id, data })
      } else {
        createAddStock(data)
      }
    })
  }

  const newStock =
    receivedStock !== null ? actualExpectedStock + receivedStock : null

  const isAdjustmentError = adjustmentError !== null

  const newStockValue =
    receivedStock === null || receivedStock === 0 || isAdjustmentError
      ? ''
      : newStock

  // Check if form is dirty
  const isFormDirty = () => {
    // Check if form fields have been touched
    const isFieldsTouched = form.isFieldsTouched()

    // Check if state values have changed
    const receivedStockChanged =
      receivedStock !== initialReceivedStockRef.current
    const additionalDetailsChanged =
      showAdditionalDetails !== initialShowAdditionalDetailsRef.current

    // Check if file list has changed (compare by length and file keys/names)
    const fileListChanged = (() => {
      if (fileList.length !== initialFileListRef.current.length) return true
      const currentKeys = fileList
        .map((f) => f.uid || f.name || f.key)
        .filter(Boolean)
      const initialKeys = initialFileListRef.current
        .map((f) => f.uid || f.name || f.key)
        .filter(Boolean)
      if (currentKeys.length !== initialKeys.length) return true
      return currentKeys.some((key, idx) => key !== initialKeys[idx])
    })()

    // Check if form values differ from initial values
    const formValues = form.getFieldsValue()
    const valuesChanged = Object.keys(formValues).some((key) => {
      const currentValue = formValues[key]
      const initialValue = initialValues[key as keyof typeof initialValues]

      // Handle dayjs date comparison
      if (dayjs.isDayjs(currentValue) && dayjs.isDayjs(initialValue)) {
        return !currentValue.isSame(initialValue)
      }
      if (dayjs.isDayjs(currentValue) && !initialValue) return true
      if (!currentValue && dayjs.isDayjs(initialValue)) return true

      return currentValue !== initialValue
    })

    return (
      isFieldsTouched ||
      receivedStockChanged ||
      fileListChanged ||
      additionalDetailsChanged ||
      valuesChanged
    )
  }

  const handleCancel = () => {
    if (isFormDirty()) {
      Modal.confirm({
        title: t('MODAL_TITLE', { ns: NAMESPACE.GENERAL }),
        content: t('MODAL_CONTENT', { ns: NAMESPACE.GENERAL }),
        okText: t('EXIT_WITHOUT_SAVING', { ns: NAMESPACE.GENERAL }),
        cancelText: t('CANCEL'),
        onOk: () => {
          form.resetFields()
          onCancel()
        }
      })
    } else {
      onCancel()
    }
  }

  return (
    <Modal
      open={open}
      onCancel={handleCancel}
      footer={null}
      width={648}
      title={
        <TitleContainer>
          <Delivery />
          <ModalTitle>
            <Heading level={3}>
              {editMode ? t('EDIT_ADDED_STOCK') : t('ADD_STOCK')}
            </Heading>
          </ModalTitle>
        </TitleContainer>
      }
    >
      <Divider />

      <Form form={form} initialValues={initialValues} preserve={false}>
        <ModalContentContainer>
          <FormCard level={2}>
            <FormItem
              name="description"
              label={<Text>{t('DESCRIPTION')}</Text>}
              required
              full
              rules={[
                { required: true, message: t('DESCRIPTION_REQUIRED') },
                { max: 150, message: t('DESCRIPTION_MAX_LENGTH') }
              ]}
            >
              <StyledTextArea
                rows={4}
                placeholder={t('ENTER_DESCRIPTION')}
                maxLength={150}
                showCount
                data-testid="add-stock-description"
              />
            </FormItem>
          </FormCard>

          <FormCard
            level={2}
            title={
              <CardTitleContainer>
                <MedicationNameHeading weight="bold" level={5}>
                  {medicationName}
                </MedicationNameHeading>
              </CardTitleContainer>
            }
          >
            <AdjustmentContainer>
              <CalculationContainer>
                <CalculationColumn $align="center" $width="140px">
                  <Label>{t('EXPECTED_STOCK')}</Label>
                  <Value weight="bold">
                    {actualExpectedStock} {displayUnit}
                  </Value>
                </CalculationColumn>
                <IconWrapper>
                  <Arrow direction="right" size={18} />
                </IconWrapper>
                <CalculationColumn $flex>
                  <LabelContainer>
                    <Asterix />
                    <Label>{t('RECEIVED_STOCK')}</Label>
                  </LabelContainer>
                  <Input
                    type="number"
                    min="1"
                    step="1"
                    value={receivedStock ?? ''}
                    onChange={(e) => handleReceivedStockChange(e.target.value)}
                    disabled={editMode}
                    status={adjustmentError ? 'error' : undefined}
                    style={{ width: '100%' }}
                    data-testid="received-stock-input"
                    suffix={displayUnit}
                  />
                </CalculationColumn>
                <IconWrapper>
                  <Arrow direction="right" size={18} />
                </IconWrapper>
                <CalculationColumn $align="center" $width="140px">
                  <Label>{t('NEW_STOCK')}</Label>
                  <Value weight="bold">
                    {newStockValue} {newStockValue ? displayUnit : ''}
                  </Value>
                </CalculationColumn>
              </CalculationContainer>
              {adjustmentError && !editMode && (
                <StockBanner type="error">{adjustmentError}</StockBanner>
              )}
            </AdjustmentContainer>
          </FormCard>

          <FormCard level={2}>
            <FormItem
              name="receivedBy"
              label={<Text>{t('RECEIVED_BY')}</Text>}
              required
              rules={[{ required: true, message: t('RECEIVED_BY_REQUIRED') }]}
            >
              <Select
                placeholder={t('SELECT_PERSON')}
                style={{ width: '100%' }}
                showSearch
                filterOption={(input, option) =>
                  String(option?.label ?? '')
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
              >
                {carerOptions.map((option) => (
                  <Select.Option
                    key={option.value}
                    value={option.value}
                    label={option.label}
                  >
                    {option.image ? (
                      <OptionContainer>
                        <ImageContainer>{option.image}</ImageContainer>
                        <StyledText>{option.label}</StyledText>
                      </OptionContainer>
                    ) : (
                      option.label
                    )}
                  </Select.Option>
                ))}
              </Select>
            </FormItem>

            <FormItem
              name="actionTime"
              label={<Text>{t('RECEIVED_DATE')}</Text>}
            >
              <DatePicker
                style={{ width: '100%' }}
                placeholder={t('SELECT_DATE')}
                format="DD-MM-YYYY"
              />
            </FormItem>

            <FormItem
              name="receivedFrom"
              label={
                <LabelWithIcon>
                  <Text>{t('RECEIVED_FROM')}</Text>
                  <Tooltip
                    title={t('RECEIVED_FROM_TOOLTIP')}
                    color={theme.color.neutral.color3}
                    overlayInnerStyle={{
                      color: '#fff',
                      borderRadius: '9px',
                      padding: '10px 16px'
                    }}
                  >
                    <QuestionIcon>
                      <QuestionMarkIcon />
                    </QuestionIcon>
                  </Tooltip>
                </LabelWithIcon>
              }
            >
              <Input placeholder={t('TYPE_HERE')} />
            </FormItem>
          </FormCard>

          <AdditionalDetailsSection
            showAdditionalDetails={showAdditionalDetails}
            onToggle={setShowAdditionalDetails}
            fileList={fileList}
            onFileChange={handleFileChange}
          />
        </ModalContentContainer>
      </Form>

      <Divider />

      <ButtonsContainer>
        <Button onClick={handleCancel} data-testid="cancel-btn">
          {t('CANCEL')}
        </Button>
        <Button
          variant="primary"
          onClick={handleSave}
          disabled={
            !!adjustmentError ||
            isLoading ||
            receivedStock === null ||
            receivedStock === 0
          }
          isLoading={isLoading}
          data-testid="save-btn"
        >
          {t('SAVE')}
        </Button>
      </ButtonsContainer>
    </Modal>
  )
}

const TitleContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;

  svg {
    width: 20px;
    height: 20px;
  }
`

const ModalTitle = styled.div`
  text-transform: none;
  font-weight: normal;
`

const ModalContentContainer = styled.div`
  margin: 24px 0;
  display: flex;
  flex-direction: column;
  gap: 24px;
`

const MedicationNameHeading = styled(Heading)`
  color: ${({ theme }) => theme.color.neutral.color3};
`

const CardTitleContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
`

const CalculationContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

const CalculationColumn = styled.div<{
  $align?: 'left' | 'center'
  $width?: string
  $flex?: boolean
}>`
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  padding: 0 8px;
  gap: 8px;
  align-items: ${({ $align = 'left' }) => $align};
  ${({ $width }) => $width && `width: ${$width};`}
  ${({ $flex }) => $flex && `flex: 1;`}
`

const IconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  padding: 0 8px;
`

const LabelContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`

const LabelWithIcon = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`

const QuestionIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.color.neutral.color1};
  cursor: pointer;

  svg {
    width: 16px;
    height: 16px;
  }
`

const Label = styled(Text)`
  color: ${({ theme }) => theme.color.neutral.color4};
  min-height: 24px;
`

const Value = styled(Text)`
  color: ${({ theme }) => theme.color.neutral.color1};
  align-self: center;
  min-height: 32px;
  display: flex;
  align-items: center;
`

const DiscrepancyValue = styled(Text)<{
  $isPositive: boolean
  $noDiscrepancy?: boolean
}>`
  color: ${({ $noDiscrepancy, theme }) =>
    $noDiscrepancy ? theme.color.status.success : theme.color.status.warning};
  align-self: center;
  min-height: 32px;
  display: flex;
  align-items: center;
`

const StyledTextArea = styled(TextArea)`
  width: 100%;
  margin-bottom: 16px;
`

const AdjustmentContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const ButtonsContainer = styled.div`
  margin-top: 24px;
  display: flex;
  justify-content: flex-end;
  gap: 10px;
`

const OptionContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  white-space: normal;
  word-break: break-word;
`

const ImageContainer = styled.div`
  display: flex;
  align-items: center;
`

const StyledText = styled(Text)`
  color: ${({ theme }) => theme.color.neutral.color3};
`

export default AddStockModal
